// Labs tab focus (Pulse "pull up the lipid trend", owner 10-21): which panel,
// test and year to show for a { analyte, panel } request, and that test's
// trend points. `panels` is the labs/patient/{pid}/get shape:
// [{ panel_name, tests: [{ testname, refrange, results: [{ date, value, refrange }] }] }].

const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

function latestYear(test) {
  const years = (test?.results || [])
    .map((r) => new Date(r.date).getFullYear())
    .filter((y) => !Number.isNaN(y));
  return years.length ? String(Math.max(...years)) : null;
}

function bestTest(cands) {
  // most results first (the longest trend), then the newest result
  const newest = (t) => Math.max(0, ...(t.results || []).map((r) => new Date(r.date).getTime() || 0));
  return cands.sort((a, b) => (b.test.results || []).length - (a.test.results || []).length
    || newest(b.test) - newest(a.test))[0];
}

// { panel, test, year } for the focus, or null when nothing on file matches.
export function findLabFocus(panels, focus) {
  const want = norm(focus?.analyte);
  if (!Array.isArray(panels) || !panels.length || (!want && !focus?.panel)) return null;
  const all = [];
  panels.forEach((p) => (p?.tests || []).forEach((t) => all.push({ panel: p, test: t })));
  const pick = (cands) => {
    if (!cands.length) return null;
    const c = bestTest(cands);
    return { panel: c.panel.panel_name, test: c.test.testname, year: latestYear(c.test) };
  };
  if (want) {
    const exact = all.filter((c) => norm(c.test.testname) === want);
    if (exact.length) return pick(exact);
    const words = new RegExp(`(^| )${want.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}( |$)`);
    const contains = all.filter((c) => words.test(norm(c.test.testname))
      || (norm(c.test.testname).length >= 3 && new RegExp(`(^| )${norm(c.test.testname).replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}( |$)`).test(want)));
    if (contains.length) return pick(contains);
  }
  const panelWant = norm(focus?.panel) || want;
  const panel = panels.find((p) => norm(p?.panel_name) === panelWant)
    || panels.find((p) => panelWant && norm(p?.panel_name).includes(panelWant));
  if (panel) {
    // the panel's newest year across its tests
    const years = (panel.tests || []).map(latestYear).filter(Boolean);
    return { panel: panel.panel_name, test: null, year: years.length ? String(Math.max(...years.map(Number))) : null };
  }
  return null;
}

// Chronological numeric points of `testName` in `panelName` (non-numeric
// results such as "Negative" are left out of the line).
export function labTrendPoints(panels, panelName, testName) {
  const panel = (panels || []).find((p) => p?.panel_name === panelName);
  const test = (panel?.tests || []).find((t) => t?.testname === testName);
  return (test?.results || [])
    .map((r) => ({ date: r.date, value: parseFloat(r.value), raw: r.value }))
    .filter((r) => r.date && !Number.isNaN(r.value))
    .sort((a, b) => new Date(a.date) - new Date(b.date));
}
